/**
 * AnimalShogi の駒を描画する。
 *
 * 実物の駒は**積み木の四角いブロック**で、 将棋の五角形ではない。 木のブロックに
 * 動物の絵が印刷されている。
 *
 * - 台 (ブロック) は焼いた PNG。 絵はベクタのまま上に重ねる。 隆起させない。
 *   → docs/design/MATERIAL_RENEWAL.md §3.8 の B 類 (記号が identity だが記号は物ではない)
 * - 各駒は **正面向きの動物顔** をシンプルなアイコンとして描く (側面の図解風 NG)。
 *   小サイズでも一目で識別できることを最優先。 識別は絵の細部ではなく輪郭で付ける。
 * - `flip` で 180 度回転 (相手の駒)。 台ごと回るので木目の向きも変わる。
 * - 絵の viewBox は 100×120 のまま。 これを**ブロックの天面**に合わせて縮めて置く。
 *   合わせる先は画像全体ではない。 画像 60mm のうち天面は中央の 38mm しかなく、
 *   さらに角丸 3mm と面取り 1.8mm があるので、 実際に刷れるのは 34.4mm。
 */
import type { AnimalPiece } from '../../games/animal';

const BLOCK_MINE = `${import.meta.env.BASE_URL}generated/animal/block.png`;
const BLOCK_OPP = `${import.meta.env.BASE_URL}generated/animal/block-opp.png`;
const BLOCK_SHADOW = `${import.meta.env.BASE_URL}generated/animal/block-shadow.png`;

interface Props {
  kind: AnimalPiece;
  variant: 'mine' | 'opp';
  flip?: boolean;
  hand?: boolean;
}

export function AnimalPieceGlyph({ kind, variant, flip, hand }: Props) {
  const ink = variant === 'mine' ? '#2a1d0c' : '#3a2a14';
  // 5 種の絵の外接矩形を実測すると、 素の座標で y 15〜102 / x 8〜92 を使う。
  // 縦の中心は 60 ではなく 58.5、 そこからの最大半径は 43.5 (+ 線幅の半分 1.5)。
  // 刷れる範囲は画像 60mm 中央の 34.4mm = viewBox 半径 28.67 なので 28.67/45 = 0.637。
  // これより大きくすると、 きりんの脚やぞうの鼻が天面を越えて面取りや小口に乗る。
  const scale = hand ? 0.59 : 0.635;

  // ブロックと接地影は**回さない**。 光は盤に固定なので、 台ごと 180 度回すと
  // 面取りのハイライトと影が反対側に付いてしまう。 回すのは絵だけでよい
  // (実物の積み木は正方形なので、 向きは絵でしか表さない)。
  return (
    <span className={`a-piece ${variant}`} aria-hidden>
      {!hand && <img className="a-shadow" src={BLOCK_SHADOW} alt="" />}
      <img className="a-block" src={variant === 'mine' ? BLOCK_MINE : BLOCK_OPP} alt="" />
      <svg
        className="a-glyph"
        viewBox="0 0 100 100"
        style={flip ? { transform: 'rotate(180deg)' } : undefined}
        aria-hidden
        focusable="false"
      >
        <g transform={`translate(50 50) scale(${scale}) translate(-50 -58.5)`}>
          <PieceSymbol kind={kind} ink={ink} />
        </g>
      </svg>
    </span>
  );
}

function PieceSymbol({ kind, ink }: { kind: AnimalPiece; ink: string }) {
  switch (kind) {
    case 'Lion':
      return <LionSymbol ink={ink} />;
    case 'Giraffe':
      return <GiraffeSymbol ink={ink} />;
    case 'Elephant':
      return <ElephantSymbol ink={ink} />;
    case 'Chick':
      return <ChickSymbol ink={ink} />;
    case 'Hen':
      return <HenSymbol ink={ink} />;
  }
}

/** 目: 黒目 + 小さなハイライト。 全種で共通にして系統を揃える。 */
function Eye({ x, y, r, ink }: { x: number; y: number; r: number; ink: string }) {
  return (
    <>
      <circle cx={x} cy={y} r={r} fill={ink} />
      <circle cx={x + r * 0.34} cy={y - r * 0.34} r={r * 0.32} fill="#fff" />
    </>
  );
}

/** ライオン (王): 尖ったたてがみの輪 + 正面顔。 輪郭の「ギザギザの円」が識別点。 */
function LionSymbol({ ink }: { ink: string }) {
  const N = 14;
  const pts: string[] = [];
  for (let i = 0; i < N; i++) {
    const a = (i / N) * Math.PI * 2 - Math.PI / 2;
    const a2 = ((i + 0.5) / N) * Math.PI * 2 - Math.PI / 2;
    const x1 = 50 + Math.cos(a) * 38;
    const y1 = 64 + Math.sin(a) * 38;
    const x2 = 50 + Math.cos(a2) * 27;
    const y2 = 64 + Math.sin(a2) * 27;
    pts.push(`${i ? 'L' : 'M'} ${x1.toFixed(1)} ${y1.toFixed(1)} L ${x2.toFixed(1)} ${y2.toFixed(1)}`);
  }
  const mane = `${pts.join(' ')} Z`;
  return (
    <g>
      <path d={mane} fill="#e79a45" stroke={ink} strokeWidth="2.6" strokeLinejoin="round" />
      <circle cx="50" cy="64" r="23" fill="#ffe9c4" stroke={ink} strokeWidth="2.4" />
      <Eye x={41} y={59} r={3.4} ink={ink} />
      <Eye x={59} y={59} r={3.4} ink={ink} />
      <path d="M 50 66 L 45 71 L 55 71 Z" fill={ink} />
      <path
        d="M 50 71 L 50 75 M 50 75 Q 44 79 40 74 M 50 75 Q 56 79 60 74"
        fill="none"
        stroke={ink}
        strokeWidth="2.4"
        strokeLinecap="round"
      />
    </g>
  );
}

/** きりん: 耳を張らせた対称の顔。 顔は縦長のまま短くし、 口元を丸く。
 *  目を非対称に置くと不気味になるので対称に。 輪郭の「縦の長さ + 張った耳」が識別点。 */
function GiraffeSymbol({ ink }: { ink: string }) {
  return (
    <g>
      <path d="M 32 46 Q 15 39 12 48 Q 11 57 29 58 Z" fill="#e8b93f" stroke={ink} strokeWidth="2.4" strokeLinejoin="round" />
      <path d="M 68 46 Q 85 39 88 48 Q 89 57 71 58 Z" fill="#e8b93f" stroke={ink} strokeWidth="2.4" strokeLinejoin="round" />
      <path d="M 43 32 L 41 21" stroke={ink} strokeWidth="3" strokeLinecap="round" />
      <path d="M 57 32 L 59 21" stroke={ink} strokeWidth="3" strokeLinecap="round" />
      <circle cx="40.6" cy="19" r="4" fill="#8a5a18" stroke={ink} strokeWidth="2.2" />
      <circle cx="59.4" cy="19" r="4" fill="#8a5a18" stroke={ink} strokeWidth="2.2" />
      <path
        d="M 50 30 Q 69 32 70 54 L 67 82 Q 67 98 50 98 Q 33 98 33 82 L 30 54 Q 31 32 50 30 Z"
        fill="#f5c84a"
        stroke={ink}
        strokeWidth="2.6"
        strokeLinejoin="round"
      />
      <circle cx="38" cy="64" r="5" fill="#b9791f" />
      <circle cx="63" cy="62" r="4.4" fill="#b9791f" />
      <circle cx="50" cy="40" r="3.6" fill="#b9791f" />
      <Eye x={41} y={52} r={3.8} ink={ink} />
      <Eye x={59} y={52} r={3.8} ink={ink} />
      <ellipse cx="50" cy="84" rx="13.5" ry="9.5" fill="#ffe7a8" stroke={ink} strokeWidth="2.2" />
      <ellipse cx="45" cy="81" rx="1.9" ry="2.5" fill={ink} />
      <ellipse cx="55" cy="81" rx="1.9" ry="2.5" fill={ink} />
      <path d="M 45 88 Q 50 91 55 88" fill="none" stroke={ink} strokeWidth="2" strokeLinecap="round" />
    </g>
  );
}

/** ぞう: 大きく張り出した耳 + 中央に垂れる太い鼻。 輪郭の「横の広さ」が識別点。
 *  細い鼻筋と白い牙は 16px で毛のように散るので持たせない。 */
function ElephantSymbol({ ink }: { ink: string }) {
  return (
    <g>
      <path
        d="M 26 46 Q 8 46 8 68 Q 8 88 28 86 Z"
        fill="#b7b1c6"
        stroke={ink}
        strokeWidth="2.6"
        strokeLinejoin="round"
      />
      <path
        d="M 74 46 Q 92 46 92 68 Q 92 88 72 86 Z"
        fill="#b7b1c6"
        stroke={ink}
        strokeWidth="2.6"
        strokeLinejoin="round"
      />
      <path
        d="M 26 62 Q 26 40 50 38 Q 74 40 74 62 Q 74 80 66 86 L 34 86 Q 26 80 26 62 Z"
        fill="#d3cde0"
        stroke={ink}
        strokeWidth="2.6"
        strokeLinejoin="round"
      />
      <Eye x={39} y={58} r={3.2} ink={ink} />
      <Eye x={61} y={58} r={3.2} ink={ink} />
      {/* 鼻は太い帯として塗り、 両脇に輪郭線を引く */}
      <path
        d="M 50 64 Q 52 78 48 88 Q 45 95 51 97"
        fill="none"
        stroke="#d3cde0"
        strokeWidth="11"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M 44.6 64 Q 46.8 78 42.6 88.6 Q 39.2 96 46 99"
        fill="none"
        stroke={ink}
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M 55.4 64 Q 57.2 78 53.4 87.4 Q 51 92.6 54 94"
        fill="none"
        stroke={ink}
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M 46 72 L 54 71 M 46 79 L 53 78"
        stroke={ink}
        strokeWidth="1.6"
        strokeLinecap="round"
        opacity="0.55"
      />
    </g>
  );
}

/** ひよこ: まんまるの黄色 + くちばし。 */
function ChickSymbol({ ink }: { ink: string }) {
  return (
    <g>
      <path d="M 50 30 Q 47 20 54 22" fill="none" stroke={ink} strokeWidth="2.6" strokeLinecap="round" />
      <circle cx="50" cy="66" r="31" fill="#ffdf5c" stroke={ink} strokeWidth="2.6" />
      <Eye x={39} y={60} r={3.8} ink={ink} />
      <Eye x={61} y={60} r={3.8} ink={ink} />
      <circle cx="30" cy="74" r="4.6" fill="#ffb0ae" opacity="0.75" />
      <circle cx="70" cy="74" r="4.6" fill="#ffb0ae" opacity="0.75" />
      <path
        d="M 40 70 L 50 76 L 60 70 L 50 84 Z"
        fill="#ff9b3d"
        stroke={ink}
        strokeWidth="2.2"
        strokeLinejoin="round"
      />
    </g>
  );
}

/** にわとり (= 成りひよこ): ひよこも丸なので、 同じ正円だと輪郭で区別が付かない。
 *  翼を張り出させて丸を崩し、 体は縦長の卵に。 地色は真っ白を避けて温かいクリームに。 */
function HenSymbol({ ink }: { ink: string }) {
  return (
    <g>
      <path d="M 27 62 Q 14 70 18 86 Q 30 85 33 72 Z" fill="#edcf92" stroke={ink} strokeWidth="2.4" strokeLinejoin="round" />
      <path d="M 73 62 Q 86 70 82 86 Q 70 85 67 72 Z" fill="#edcf92" stroke={ink} strokeWidth="2.4" strokeLinejoin="round" />
      <path
        d="M 35 40 Q 34 27 43 29 Q 45 17 52 26 Q 61 20 63 33 Q 68 38 63 44 Z"
        fill="#e2453c"
        stroke={ink}
        strokeWidth="2.6"
        strokeLinejoin="round"
      />
      <ellipse cx="50" cy="68" rx="26" ry="30" fill="#f8e5bb" stroke={ink} strokeWidth="2.6" />
      <Eye x={39} y={62} r={3.8} ink={ink} />
      <Eye x={61} y={62} r={3.8} ink={ink} />
      <path d="M 40 72 L 50 78 L 60 72 L 50 86 Z" fill="#ffab2e" stroke={ink} strokeWidth="2.2" strokeLinejoin="round" />
      <path d="M 44 87 Q 42 99 50 101 Q 58 99 56 87 Z" fill="#e2453c" stroke={ink} strokeWidth="2.4" strokeLinejoin="round" />
    </g>
  );
}
